import Image from 'next/image'
import Link from 'next/link'
import { useState } from 'react'
import { cropImageFromCdn, getPostsForPage } from '../../lib/fetchBlog'
import { BlogDateOptions, formatDate } from '../../util/formatDate'
import { BlogPost } from './BlogTypes'

type BlogListProps = {
  posts: BlogPost[]
}

export default function BlogList({ posts = [] }: BlogListProps): JSX.Element {
  const [postsToShow, setPostsToShow] = useState<BlogPost[]>(posts)
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(2)

  async function fetchMorePosts() {
    try {
      setLoading(true)
      const response = await getPostsForPage(page)
      // Only show Load More if we got new posts
      if (response.data.length > 0) {
        setPostsToShow([...postsToShow, ...response.data])
        setPage(page + 1)
      } else {
        // Hide the Load More button by setting loading to true permanently
        setLoading(true)
      }
    } catch (error) {
      console.error(error)
    } finally {
      if (loading) setLoading(false)
    }
  }

  return (
    <div>
      <h1 className="font-medium text-xl mb-8">Recent Articles</h1>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {postsToShow.map((post) => (
          <PostPreviewCard post={post} key={post.slug} />
        ))}
      </div>

      {postsToShow.length > 0 && !loading && (
        <div className="flex justify-center my-10">
          <div 
            className="underline font-medium cursor-pointer" 
            onClick={fetchMorePosts}
          >
            Load More
          </div>
        </div>
      )}
      
      {loading && (
        <div className="flex justify-center my-10">
          <div className="font-medium">Loading...</div>
        </div>
      )}
    </div>
  )
}

interface PostPreviewCardProps {
  post: BlogPost
}

export function PostPreviewCard({ post }: PostPreviewCardProps): JSX.Element {
  return (
    <Link key={post.slug} href={`/post/${post.slug}`} className="block">
      <div className="rounded-lg flex items-center gap-6">
        <div className="flex-shrink-0 w-32 h-32">
          <Image
            src={cropImageFromCdn(post.featured_image, 150, 150)}
            width={150}
            height={150}
            sizes="(max-width: 768px) 100vw,
              (max-width: 1200px) 50vw,
              33vw"
            alt={post.featured_image_alt ?? ''}
            className="rounded-lg"
          />
        </div>

        <div>
          <h1 className="font-medium mb-2">{post.title}</h1>
          <p className="text-gray-500 italic text-sm">
            By {post.author?.first_name} {post.author?.last_name}
          </p>
        </div>
      </div>
    </Link>
  )
}

interface AuthorBlockProps {
  name: string
  avatarUrl: string
  publishDateString: string
}

export function AuthorBlock({
  name,
  avatarUrl,
  publishDateString,
}: AuthorBlockProps): JSX.Element {
  const AUTHOR_PROFILE_IMAGE_SIZE = 60

  return (
    <div className="flex items-center pb-2">
      <div className="mr-4 w-12 h-12">
        <Image
          src={cropImageFromCdn(
            avatarUrl,
            AUTHOR_PROFILE_IMAGE_SIZE,
            AUTHOR_PROFILE_IMAGE_SIZE,
          )}
          alt={name}
          width={AUTHOR_PROFILE_IMAGE_SIZE}
          height={AUTHOR_PROFILE_IMAGE_SIZE}
          sizes="(max-width: 768px) 100vw,
              (max-width: 1200px) 50vw,
              33vw"
          className="rounded-full"
        />
      </div>

      <div>
        <h1 className="font-semibold leading-none">{name}</h1>
        <time className="text-sm">
          {formatDate(publishDateString, BlogDateOptions)}
        </time>
      </div>
    </div>
  )
}
